<template>
  <v-sheet border rounded class="rich-text-editor">
    <div v-if="editor" class="rich-text-editor__toolbar d-flex flex-wrap ga-1 pa-1">
      <v-btn
        v-for="action in actions"
        :key="action.name"
        :icon="action.icon"
        :title="action.title"
        :active="action.isActive ? action.isActive() : false"
        :disabled="action.isDisabled ? action.isDisabled() : false"
        size="small"
        variant="text"
        density="comfortable"
        @click="action.run()"
      ></v-btn>
    </div>
    <v-divider></v-divider>
    <editor-content :editor="editor" class="rich-text-editor__content pa-3" />
  </v-sheet>
</template>

<script>
  // Namespace import: with named imports, @vue/vue3-jest gives this module and "vue" the same `_vue` binding.
  import * as TiptapVue from "@tiptap/vue-3";
  import StarterKit from "@tiptap/starter-kit";

  const { Editor, EditorContent } = TiptapVue;

  // Rich-text (HTML) editor with v-model, using the same formatting set as the former tiptap-vuetify editor.
  export default {
    name: "RichTextEditor",
    components: { EditorContent },
    props: {
      modelValue: {
        type: String,
        default: "",
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        editor: null,
      };
    },
    computed: {
      actions() {
        const chain = () => this.editor.chain().focus();
        const isActive = (name, attributes) => () => this.editor.isActive(name, attributes);
        return [
          { name: "bold", icon: "mdi-format-bold", title: "Gras", isActive: isActive("bold"), run: () => chain().toggleBold().run() },
          { name: "italic", icon: "mdi-format-italic", title: "Italique", isActive: isActive("italic"), run: () => chain().toggleItalic().run() },
          { name: "underline", icon: "mdi-format-underline", title: "Souligné", isActive: isActive("underline"), run: () => chain().toggleUnderline().run() },
          { name: "strike", icon: "mdi-format-strikethrough", title: "Barré", isActive: isActive("strike"), run: () => chain().toggleStrike().run() },
          { name: "code", icon: "mdi-code-tags", title: "Code", isActive: isActive("code"), run: () => chain().toggleCode().run() },
          { name: "link", icon: "mdi-link-variant", title: "Lien", isActive: isActive("link"), run: () => this.editLink() },
          { name: "paragraph", icon: "mdi-format-paragraph", title: "Paragraphe", isActive: isActive("paragraph"), run: () => chain().setParagraph().run() },
          ...[1, 2, 3].map((level) => ({
            name: `heading-${level}`,
            icon: `mdi-format-header-${level}`,
            title: `Titre ${level}`,
            isActive: isActive("heading", { level }),
            run: () => chain().toggleHeading({ level }).run(),
          })),
          { name: "bulletList", icon: "mdi-format-list-bulleted", title: "Liste à puces", isActive: isActive("bulletList"), run: () => chain().toggleBulletList().run() },
          { name: "orderedList", icon: "mdi-format-list-numbered", title: "Liste numérotée", isActive: isActive("orderedList"), run: () => chain().toggleOrderedList().run() },
          { name: "blockquote", icon: "mdi-format-quote-close", title: "Citation", isActive: isActive("blockquote"), run: () => chain().toggleBlockquote().run() },
          { name: "horizontalRule", icon: "mdi-minus", title: "Ligne horizontale", run: () => chain().setHorizontalRule().run() },
          { name: "hardBreak", icon: "mdi-keyboard-return", title: "Saut de ligne", run: () => chain().setHardBreak().run() },
          { name: "undo", icon: "mdi-undo", title: "Annuler", isDisabled: () => !this.editor.can().undo(), run: () => chain().undo().run() },
          { name: "redo", icon: "mdi-redo", title: "Rétablir", isDisabled: () => !this.editor.can().redo(), run: () => chain().redo().run() },
        ];
      },
    },
    watch: {
      modelValue(value) {
        // Only sync changes made outside the editor (e.g. another section was loaded).
        if (this.editor && value !== this.editor.getHTML()) {
          this.editor.commands.setContent(value || "", { emitUpdate: false });
        }
      },
    },
    mounted() {
      this.editor = new Editor({
        content: this.modelValue || "",
        extensions: [
          StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            codeBlock: false,
            trailingNode: false,
            link: { openOnClick: false },
          }),
        ],
        onUpdate: () => {
          this.$emit("update:modelValue", this.editor.getHTML());
        },
      });
    },
    beforeUnmount() {
      if (this.editor) {
        this.editor.destroy();
      }
    },
    methods: {
      editLink() {
        const previousUrl = this.editor.getAttributes("link").href || "";
        const url = window.prompt("Adresse du lien (laisser vide pour le retirer)", previousUrl);
        if (url === null) {
          return;
        }

        const chain = this.editor.chain().focus().extendMarkRange("link");
        if (url.trim() === "") {
          chain.unsetLink().run();
        } else {
          chain.setLink({ href: url.trim() }).run();
        }
      },
    },
  };
</script>

<style scoped>
  .rich-text-editor__content :deep(.ProseMirror) {
    min-height: 200px;
    outline: none;
  }

  .rich-text-editor__content :deep(.ProseMirror > * + *) {
    margin-top: 0.75em;
  }

  .rich-text-editor__content :deep(.ProseMirror ul),
  .rich-text-editor__content :deep(.ProseMirror ol) {
    padding-left: 1.5rem;
  }

  .rich-text-editor__content :deep(.ProseMirror blockquote) {
    border-left: 3px solid rgba(var(--v-theme-on-surface), 0.2);
    padding-left: 1rem;
  }
</style>
